import clsx from 'clsx'
import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { classToFComponent, FCClassContext } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'

import {
    ehExemploDaMeta,
    filtrarTemplates,
    LIMITE_BOTOES,
    LIMITE_CORPO,
    podeEditar,
    podeExcluir,
    previa,
    problemasDoEditor,
    problemasParaUso,
    ROTULO_CATEGORIA,
    ROTULO_SITUACAO_USO,
    ROTULO_STATUS,
    ultimaLeitura,
    variaveisDoTexto
} from '../Templates.regras'
import { Editor, Template, TemplatesScope, Uso } from '../Templates.scopes'

const LOG = Logger.get('Templates-VIEW')

const quando = (iso?: string) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : ''

const useStyles = makeStyles()((theme) => ({
    view: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: theme.spacing(3),
        rowGap: theme.spacing(2.5),
        overflow: 'auto',
        minHeight: 0,
        '& > *': { flexShrink: 0 }
    },
    cabecalho: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap'
    },
    acoes: { display: 'flex', gap: theme.spacing(1) },
    card: { borderRadius: 14, boxShadow: '0px 2px 12px rgba(0,0,0,0.05)' },
    titulo: { fontWeight: 600, fontSize: 15 },
    nota: { fontSize: 13, color: theme.palette.text.secondary },
    filtros: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        flexWrap: 'wrap',
        margin: theme.spacing(1.5, 0)
    },
    chips: { display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' },
    // Balão no estilo do WhatsApp, para a prévia.
    balao: {
        backgroundColor: '#e7ffdb',
        color: '#111b21',
        borderRadius: 10,
        padding: theme.spacing(1.25, 1.5),
        whiteSpace: 'pre-wrap',
        fontSize: 14,
        maxWidth: 440,
        boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
    },
    botaoPrevia: {
        backgroundColor: '#fff',
        color: '#00a5f4',
        borderRadius: 10,
        padding: theme.spacing(0.75),
        textAlign: 'center',
        fontSize: 14,
        maxWidth: 440,
        marginTop: 4,
        boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
    },
    fundoPrevia: { backgroundColor: '#efeae2', borderRadius: 10, padding: theme.spacing(1.5) },
    campos: { display: 'flex', flexDirection: 'column', rowGap: theme.spacing(2), marginTop: theme.spacing(1) },
    linha: { display: 'flex', gap: theme.spacing(2), flexWrap: 'wrap', '& > *': { flex: '1 1 180px' } }
}))

export type TemplatesViewProps = { className?: string; scope: TemplatesScope }

class TemplatesClass implements FCClassContext<TemplatesViewProps> {
    scope!: TemplatesScope
    styles!: ReturnType<typeof useStyles>

    onSyncState({ scope }: TemplatesViewProps) {
        this.scope = scope
        this.styles = useStyles()
    }

    render({ className }: TemplatesViewProps) {
        const { scope } = this
        const { classes } = this.styles
        return (
            <div className={clsx(classes.view, className)}>
                <Box className={classes.cabecalho}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Templates do WhatsApp
                        </Typography>
                        <Typography color="text.secondary" fontSize={15}>
                            As mensagens que a LIA pode mandar fora da janela de 24 h, e qual template atende cada uso.
                        </Typography>
                    </Box>
                    <Box className={classes.acoes}>
                        <Tooltip title="Lê de novo todos os templates da conta na Meta e refaz a cópia local. Só é preciso se algo foi mudado direto no WhatsApp Manager e o aviso da Meta não chegou.">
                            <span>
                                <Button
                                    startIcon={<RefreshIcon />}
                                    onClick={() => scope.onAtualizar().catch(LOG.caught)}
                                    disabled={scope.carregando}
                                >
                                    Recarregar da Meta
                                </Button>
                            </span>
                        </Tooltip>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => scope.onAbrirCriar('')}>
                            Criar template
                        </Button>
                    </Box>
                </Box>
                {scope.carregando && <LinearProgress />}
                <Usos scope={scope} />
                <Lista scope={scope} />
                <Detalhe scope={scope} />
                <EditorDialog scope={scope} />
                <Trocar scope={scope} />
                <Excluir scope={scope} />
            </div>
        )
    }
}

function Rotulo({ status }: { status: string }) {
    const r = ROTULO_STATUS[status] ?? { rotulo: status, cor: 'default' as const }
    return <Chip size="small" label={r.rotulo} color={r.cor} variant="outlined" />
}

function Categoria({ t }: { t: Pick<Template, 'categoria' | 'categoriaAnterior'> }) {
    const chip = (
        <Chip
            size="small"
            label={ROTULO_CATEGORIA[t.categoria] ?? t.categoria}
            color={t.categoria === 'MARKETING' ? 'warning' : 'default'}
            variant="outlined"
        />
    )
    return t.categoriaAnterior ? (
        <Tooltip title={`A Meta reclassificou de ${ROTULO_CATEGORIA[t.categoriaAnterior] ?? t.categoriaAnterior}`}>
            {chip}
        </Tooltip>
    ) : (
        chip
    )
}

function Usos({ scope }: { scope: TemplatesScope }) {
    const { classes } = useStyles()
    return (
        <Card className={classes.card}>
            <CardContent>
                <Typography className={classes.titulo}>Usos da LIA</Typography>
                <Typography className={classes.nota}>
                    Cada mensagem que a LIA manda como template. O texto do template ligado é o que o parceiro lê, com a
                    janela de 24 h aberta (como texto livre) ou fechada (como template). Trocar o texto é editar o
                    template ou criar um novo, esperar a aprovação da Meta e apontar o uso para ele.
                </Typography>
                <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Uso</TableCell>
                                <TableCell>Template</TableCell>
                                <TableCell>Situação</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scope.usos.map((u) => {
                                const s = ROTULO_SITUACAO_USO[u.situacao]
                                return (
                                    <TableRow key={u.uso}>
                                        <TableCell>
                                            <div>{u.descricao}</div>
                                            <Typography className={classes.nota}>
                                                {u.uso} · variáveis {u.variaveis.map((v) => `{{${v}}}`).join(', ')}
                                                {u.botoes.length ? ` · botões: ${u.botoes.join(', ')}` : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {u.template ? (
                                                <Box className={classes.chips}>
                                                    <span>{u.template}</span>
                                                    {u.categoria && <Categoria t={{ categoria: u.categoria }} />}
                                                </Box>
                                            ) : (
                                                '—'
                                            )}
                                            {u.atualizadoPor && (
                                                <Typography className={classes.nota}>
                                                    {u.atualizadoPor}, {quando(u.atualizadoEm)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={s.rotulo} color={s.cor} />
                                            {u.fonteDoTexto && (
                                                <Typography className={classes.nota}>
                                                    {u.fonteDoTexto === 'template'
                                                        ? 'Texto: o do template.'
                                                        : 'Texto: o do código, até haver um template aprovado e compatível.'}
                                                </Typography>
                                            )}
                                            {u.problemas.map((p) => (
                                                <Typography key={p} className={classes.nota}>
                                                    {p}
                                                </Typography>
                                            ))}
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {u.texto && u.situacao === 'sem_template' && (
                                                <Button size="small" onClick={() => scope.onAbrirCriar(u.uso)}>
                                                    Criar template
                                                </Button>
                                            )}
                                            <Button size="small" onClick={() => scope.onAbrirTrocar(u.uso)}>
                                                Trocar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    )
}

function Lista({ scope }: { scope: TemplatesScope }) {
    const { classes } = useStyles()
    const lista = filtrarTemplates(scope.templates, scope.busca, scope.mostrarExemplosMeta)
    return (
        <Card className={classes.card}>
            <CardContent>
                <Typography className={classes.titulo}>Templates da conta</Typography>
                <Typography className={classes.nota}>
                    Cópia local da conta na Meta
                    {ultimaLeitura(scope.templates) ? `, lida em ${quando(ultimaLeitura(scope.templates))}` : ''}. Ela
                    se atualiza ao criar ou editar por aqui e quando a Meta avisa que aprovou, recusou ou reclassificou
                    um template. Clique no nome para ver o texto e editar.
                </Typography>
                <Box className={classes.filtros}>
                    <TextField
                        size="small"
                        placeholder="Buscar pelo nome"
                        value={scope.busca}
                        onChange={(e) => scope.onBuscaChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={scope.mostrarExemplosMeta}
                                onChange={(e) => scope.onMostrarExemplosChange(e.target.checked)}
                            />
                        }
                        label="Mostrar os exemplos da Meta"
                    />
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Idioma</TableCell>
                                <TableCell>Categoria</TableCell>
                                <TableCell>Situação</TableCell>
                                <TableCell>Qualidade</TableCell>
                                <TableCell>Usos</TableCell>
                                <TableCell padding="checkbox" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lista.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>Nenhum template.</TableCell>
                                </TableRow>
                            )}
                            {lista.map((t) => (
                                <TableRow key={t.id} hover>
                                    <TableCell>
                                        <Link
                                            component="button"
                                            underline="hover"
                                            onClick={() => scope.onAbrirDetalhe(t.id)}
                                        >
                                            {t.nome}
                                        </Link>
                                        {t.simulado && (
                                            <Chip size="small" label="simulado" sx={{ ml: 1 }} variant="outlined" />
                                        )}
                                        {t.cabecalho === 'IMAGE' && (
                                            <Chip size="small" label="com imagem" sx={{ ml: 1 }} variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell>{t.idioma}</TableCell>
                                    <TableCell>
                                        <Categoria t={t} />
                                    </TableCell>
                                    <TableCell>
                                        <Rotulo status={t.status} />
                                    </TableCell>
                                    <TableCell>
                                        {t.qualidade && t.qualidade !== 'UNKNOWN' ? t.qualidade : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Box className={classes.chips}>
                                            {t.usos.map((u) => (
                                                <Chip key={u} size="small" label={u} />
                                            ))}
                                            {(t.campanhas ?? []).map((c) => (
                                                <Chip
                                                    key={`c-${c}`}
                                                    size="small"
                                                    color="info"
                                                    label={`campanha ${c}`}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell padding="checkbox">
                                        {podeExcluir(t) && (
                                            <Tooltip title="Excluir da conta (nenhum uso nem campanha aponta para ele)">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Excluir ${t.nome} (${t.idioma})`}
                                                    onClick={() => scope.onAbrirExcluir(t.id)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    )
}

function Previa({ corpo, exemplos, botoes }: { corpo: string; exemplos: Record<string, string>; botoes: string[] }) {
    const { classes } = useStyles()
    return (
        <Box className={classes.fundoPrevia}>
            <div className={classes.balao}>{previa(corpo, exemplos) || ' '}</div>
            {botoes.map((b, i) => (
                <div key={`${b}-${i}`} className={classes.botaoPrevia}>
                    {b || ' '}
                </div>
            ))}
        </Box>
    )
}

function Detalhe({ scope }: { scope: TemplatesScope }) {
    const { classes } = useStyles()
    const t = scope.detalhe
    return (
        <Dialog open={Boolean(t)} onClose={() => scope.onFecharDetalhe()} maxWidth="sm" fullWidth>
            {t && (
                <>
                    <DialogTitle>{t.nome}</DialogTitle>
                    <DialogContent>
                        <Box className={classes.chips} sx={{ mb: 1.5 }}>
                            <Rotulo status={t.status} />
                            <Categoria t={t} />
                            <Chip size="small" label={t.idioma} variant="outlined" />
                            {t.usos.map((u) => (
                                <Chip key={u} size="small" label={`uso: ${u}`} />
                            ))}
                            {(t.campanhas ?? []).map((c) => (
                                <Chip key={`c-${c}`} size="small" color="info" label={`campanha: ${c}`} />
                            ))}
                            {t.cabecalho === 'IMAGE' && (
                                <Chip size="small" label="imagem no cabeçalho" variant="outlined" />
                            )}
                        </Box>
                        {t.motivoRecusa && (
                            <Alert severity="error" sx={{ mb: 1.5 }}>
                                Recusado: {t.motivoRecusa}
                            </Alert>
                        )}
                        <Previa corpo={t.corpo} exemplos={{}} botoes={t.botoes} />
                        <Typography className={classes.nota} sx={{ mt: 1 }}>
                            Variáveis: {t.variaveis.length ? t.variaveis.map((v) => `{{${v}}}`).join(', ') : 'nenhuma'}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => scope.onFecharDetalhe()}>Fechar</Button>
                        {podeEditar(t) ? (
                            <Button variant="outlined" onClick={() => scope.onAbrirEditar(t.id)}>
                                Editar
                            </Button>
                        ) : (
                            <Typography className={classes.nota} sx={{ mr: 1 }}>
                                Template de campanha: muda pela campanha.
                            </Typography>
                        )}
                    </DialogActions>
                </>
            )}
        </Dialog>
    )
}

function EditorDialog({ scope }: { scope: TemplatesScope }) {
    const { classes } = useStyles()
    const e = scope.editor
    const mudar = (m: Partial<Editor>) => scope.onEditorChange(m)
    const variaveis = e ? variaveisDoTexto(e.corpo) : []
    const problemas = e ? problemasDoEditor(e, e.modo === 'criar' ? scope.templates.map((t) => t.nome) : []) : []
    return (
        <Dialog open={Boolean(e)} onClose={() => scope.onFecharEditor()} maxWidth="md" fullWidth>
            {e && (
                <>
                    <DialogTitle>{e.modo === 'criar' ? 'Criar template' : `Editar ${e.nome}`}</DialogTitle>
                    <DialogContent>
                        {e.modo === 'editar' ? (
                            <Alert severity="info">
                                A Meta revisa o template de novo a cada edição, e um template aprovado só pode ser
                                editado uma vez a cada 24 h e até 10 vezes em 30 dias. Se ele atende um uso, a edição
                                não pode mudar as variáveis nem o número de botões: nesse caso, crie um template novo e
                                troque o uso.
                            </Alert>
                        ) : (
                            <Alert severity="info">
                                A Meta analisa o template antes de liberar o uso (de minutos a 24 h) e pode mudar a
                                categoria: um aviso genérico tende a virar Marketing, mais caro. Citar um fato concreto
                                (empresa, CNPJ, negócio) ajuda a ficar como Utilidade.
                                {e.paraUso && ` O texto veio do uso "${e.paraUso}": mantenha-o igual ao do código.`}
                            </Alert>
                        )}
                        <Box className={classes.campos}>
                            <Box className={classes.linha}>
                                <TextField
                                    size="small"
                                    label="Nome"
                                    value={e.nome}
                                    disabled={e.modo === 'editar'}
                                    onChange={(ev) => mudar({ nome: ev.target.value.trim() })}
                                    helperText={
                                        e.modo === 'criar' ? 'Minúsculas, dígitos e "_"; ex.: lia_aviso_ag_taxtech' : ''
                                    }
                                />
                                <TextField
                                    select
                                    size="small"
                                    label="Categoria pedida"
                                    value={e.categoria}
                                    disabled={e.modo === 'editar'}
                                    onChange={(ev) => mudar({ categoria: ev.target.value as Editor['categoria'] })}
                                >
                                    <MenuItem value="UTILITY">Utilidade</MenuItem>
                                    <MenuItem value="MARKETING">Marketing</MenuItem>
                                </TextField>
                                <TextField
                                    size="small"
                                    label="Idioma"
                                    value={e.idioma}
                                    disabled={e.modo === 'editar'}
                                    onChange={(ev) => mudar({ idioma: ev.target.value.trim() })}
                                />
                            </Box>
                            <TextField
                                label="Texto"
                                multiline
                                minRows={5}
                                value={e.corpo}
                                onChange={(ev) => mudar({ corpo: ev.target.value })}
                                helperText={`${e.corpo.length}/${LIMITE_CORPO} · variáveis em {{nome}}, com letras minúsculas`}
                            />
                            {variaveis.length > 0 && (
                                <Box>
                                    <Typography className={classes.titulo} fontSize={14}>
                                        Exemplo de cada variável
                                    </Typography>
                                    <Typography className={classes.nota}>
                                        A Meta exige um exemplo para analisar o template.
                                    </Typography>
                                    <Box className={classes.linha} sx={{ mt: 1 }}>
                                        {variaveis.map((v) => (
                                            <TextField
                                                key={v}
                                                size="small"
                                                label={`{{${v}}}`}
                                                value={e.exemplos[v] ?? ''}
                                                onChange={(ev) =>
                                                    mudar({ exemplos: { ...e.exemplos, [v]: ev.target.value } })
                                                }
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            <Box>
                                <Typography className={classes.titulo} fontSize={14}>
                                    Botões de resposta rápida
                                </Typography>
                                {e.botoes.map((b, i) => (
                                    <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                                        <TextField
                                            size="small"
                                            value={b}
                                            placeholder="Texto do botão"
                                            onChange={(ev) =>
                                                mudar({
                                                    botoes: e.botoes.map((x, j) => (j === i ? ev.target.value : x))
                                                })
                                            }
                                            sx={{ flex: 1, maxWidth: 360 }}
                                        />
                                        <IconButton
                                            aria-label="Tirar o botão"
                                            onClick={() => mudar({ botoes: e.botoes.filter((_, j) => j !== i) })}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                                {e.botoes.length < LIMITE_BOTOES && (
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        sx={{ mt: 1 }}
                                        onClick={() => mudar({ botoes: [...e.botoes, ''] })}
                                    >
                                        Adicionar botão
                                    </Button>
                                )}
                            </Box>
                            <Box>
                                <Typography className={classes.titulo} fontSize={14} sx={{ mb: 1 }}>
                                    Prévia
                                </Typography>
                                <Previa corpo={e.corpo} exemplos={e.exemplos} botoes={e.botoes} />
                            </Box>
                            {problemas.length > 0 && (
                                <Alert severity="warning">
                                    {problemas.map((p) => (
                                        <div key={p}>{p}</div>
                                    ))}
                                </Alert>
                            )}
                            {scope.erroEditor && <Alert severity="error">{scope.erroEditor}</Alert>}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => scope.onFecharEditor()}>Cancelar</Button>
                        <Button
                            variant="contained"
                            disabled={scope.salvando || problemas.length > 0}
                            onClick={() => scope.onSalvarEditor().catch(LOG.caught)}
                        >
                            {scope.salvando
                                ? 'Enviando…'
                                : e.modo === 'criar'
                                ? 'Enviar para aprovação'
                                : 'Salvar edição'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    )
}

function Trocar({ scope }: { scope: TemplatesScope }) {
    const { classes } = useStyles()
    const uso = scope.trocar
    const [escolha, setEscolha] = React.useState('')
    React.useEffect(() => setEscolha(uso?.template ? `${uso.template}|${uso.idioma}` : ''), [uso])
    const candidatos: { t: Template; problemas: string[] }[] = uso
        ? scope.templates
              .filter((t) => t.status === 'APPROVED' && !ehExemploDaMeta(t.nome))
              .map((t) => ({ t, problemas: problemasParaUso(uso as Uso, t) }))
              .sort((a, b) => a.problemas.length - b.problemas.length || a.t.nome.localeCompare(b.t.nome))
        : []
    const escolhido = candidatos.find((c) => `${c.t.nome}|${c.t.idioma}` === escolha)
    return (
        <Dialog open={Boolean(uso)} onClose={() => scope.onFecharTrocar()} maxWidth="md" fullWidth>
            {uso && (
                <>
                    <DialogTitle>Template do uso: {uso.descricao}</DialogTitle>
                    <DialogContent>
                        <Typography className={classes.nota}>
                            O uso preenche {uso.variaveis.map((v) => `{{${v}}}`).join(', ')}
                            {uso.botoes.length ? ` e espera os botões ${uso.botoes.join(', ')}` : ' e não tem botões'}.
                            Só serve template aprovado, com as mesmas variáveis nomeadas e o mesmo número de botões.
                        </Typography>
                        <TableContainer sx={{ mt: 1, maxHeight: 420 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>Template</TableCell>
                                        <TableCell>Categoria</TableCell>
                                        <TableCell>Serve?</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {candidatos.map(({ t, problemas }) => {
                                        const valor = `${t.nome}|${t.idioma}`
                                        return (
                                            <TableRow
                                                key={t.id}
                                                hover
                                                selected={valor === escolha}
                                                onClick={() => problemas.length === 0 && setEscolha(valor)}
                                                sx={{ cursor: problemas.length ? 'default' : 'pointer' }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Radio
                                                        checked={valor === escolha}
                                                        disabled={problemas.length > 0}
                                                        onChange={() => setEscolha(valor)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {t.nome}
                                                    <Typography className={classes.nota}>{t.idioma}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Categoria t={t} />
                                                </TableCell>
                                                <TableCell>
                                                    {problemas.length ? (
                                                        <Typography className={classes.nota}>
                                                            {problemas.join('; ')}
                                                        </Typography>
                                                    ) : (
                                                        <Chip size="small" label="Serve" color="success" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {escolhido?.t.categoria === 'MARKETING' && (
                            <Alert severity="warning" sx={{ mt: 1.5 }}>
                                Template de Marketing: cada envio custa mais, e a Meta pode segurar a entrega (131049).
                            </Alert>
                        )}
                        {scope.erroTrocar && (
                            <Alert severity="error" sx={{ mt: 1.5 }}>
                                {scope.erroTrocar}
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => scope.onFecharTrocar()}>Cancelar</Button>
                        <Button
                            variant="contained"
                            disabled={!escolhido || scope.salvando || escolha === `${uso.template}|${uso.idioma}`}
                            onClick={() => scope.onLigar(escolha).catch(LOG.caught)}
                        >
                            Usar este template
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    )
}

function Excluir({ scope }: { scope: TemplatesScope }) {
    const t = scope.excluir
    return (
        <Dialog open={Boolean(t)} onClose={() => scope.onFecharExcluir()} maxWidth="xs" fullWidth>
            {t && (
                <>
                    <DialogTitle>Excluir {t.nome}?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            O template sai da conta na Meta, só no idioma {t.idioma}. Nenhum uso da LIA aponta para ele.
                        </Typography>
                        {t.status === 'APPROVED' && !t.simulado && (
                            <Alert severity="warning" sx={{ mt: 1.5 }}>
                                A Meta não deixa criar outro template com este nome por um tempo (cerca de 30 dias).
                            </Alert>
                        )}
                        {scope.erroExcluir && (
                            <Alert severity="error" sx={{ mt: 1.5 }}>
                                {scope.erroExcluir}
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => scope.onFecharExcluir()}>Cancelar</Button>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={scope.salvando}
                            onClick={() => scope.onExcluir().catch(LOG.caught)}
                        >
                            Excluir
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    )
}

export const TemplatesView = classToFComponent(TemplatesClass, React)
export default TemplatesView
